import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { TextField, Button } from '../../primitives';
import { getCsrfToken } from '../../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * ResourceForm
 * Reusable resource creation form with simple styling.
 *
 * - Can be embedded inside a Dialog or used standalone.
 * - By default it does NOT render actions. Set `showActions` for Submit button.
 * - Parent can trigger submission via `registerSubmit`.
 */
const ResourceForm = ({
  facilityId,
  onSaved,
  showActions = false,
  submitLabel = 'Submit',
  registerSubmit,
  initialData = null,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    schedule_url: '',
    facility: facilityId,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      facility: facilityId,
    }));
  }, [facilityId]);

  // Initialize form data if provided
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        facility: facilityId,
      }));
    }
  }, [initialData, facilityId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const submit = async () => {
    const requiredErrors = {};
    if (!formData.name?.trim()) requiredErrors.name = 'Resource name is required.';
    if (!formData.schedule_url?.trim()) requiredErrors.schedule_url = 'Schedule URL is required.';

    if (Object.keys(requiredErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...requiredErrors }));
      setErrorMessage('Please fill in all required fields.');
      return false;
    }

    setLoading(true);
    setErrorMessage('');
    setErrors({});

    try {
      const requestData = {
        name: formData.name,
        schedule_url: formData.schedule_url,
        facility: facilityId,
      };

      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/resources/`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.name || data.schedule_url || data.facility) {
          setErrors(data);
        } else {
          setErrorMessage(
            data.error || data.message || 'Failed to create resource. Please try again.'
          );
        }
        setLoading(false);
        return false;
      }

      if (onSaved) onSaved(data);
      setFormData({
        name: '',
        schedule_url: '',
        facility: facilityId,
      });
      setLoading(false);
      return true;
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      console.error('Error creating resource:', err);
      setLoading(false);
      return false;
    }
  };

  // Let parent trigger submit
  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(submit);
  }, [registerSubmit, submit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submit();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Typography
        variant="body1"
        sx={{
          mb: 2,
          color: '#000000',
          fontWeight: 600,
        }}
      >
        Add a new resource:
      </Typography>

      <TextField
        fullWidth
        placeholder="Resource Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        error={!!errors.name}
        helperText={errors.name}
        required
        margin="normal"
        autoComplete="off"
        autoFocus
      />

      <TextField
        fullWidth
        placeholder="Schedule URL"
        name="schedule_url"
        value={formData.schedule_url}
        onChange={handleChange}
        error={!!errors.schedule_url}
        helperText={errors.schedule_url}
        required
        margin="normal"
        autoComplete="off"
      />

      {showActions && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              submitLabel
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ResourceForm;